import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Grid, Icon, Tabs, Tab, Dialog, Container, Typography, CardContent } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftBadge from 'components/SoftBadge';
import DataTable from 'examples/Tables/DataTable';
import { partnerService } from 'services/partnerService';
import CustomerAddForm from './CustomerAddForm';
import CustomerServices from './CustomerServices';
import PartnerBrandingSettings from './PartnerBrandingSettings';
import CustomerPriceDialog from './CustomerPriceDialog';

const PartnerDashboard = ({ userId }) => {
  const [customers, setCustomers] = useState([]);
  const [iaasStats, setIaasStats] = useState({ count: 0 });
  const [saasStats, setSaasStats] = useState({ count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [createError, setCreateError] = useState('');
  const [priceDialogCustomer, setPriceDialogCustomer] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDocTab, setActiveDocTab] = useState(0);
  const [documentCategories, setDocumentCategories] = useState(['Whitepapers', 'Präsentationen', 'Preislisten']);
  const [documents, setDocuments] = useState([
    //demo in readme
  ]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [customersData, iaasData, saasData] = await Promise.all([
        partnerService.getCustomers(userId),
        partnerService.getIaasStats(userId),
        partnerService.getSaasStats(userId)
      ]);
      setCustomers(customersData);
      setIaasStats(iaasData);
      setSaasStats(saasData);
    } catch (error) {
      console.error('Fehler beim Laden der Partner-Daten:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleDocTabChange = (_, newValue) => {
    setActiveDocTab(newValue);
  };
  
  // Define the cell component separately
  const DownloadCell = ({ value }) => (
    <SoftButton variant="text" color="info" size="small" component="a" href={value}>
      <Icon>download</Icon>
    </SoftButton>
  );
  
  // Add prop-types validation for the cell component
  DownloadCell.propTypes = {
    value: PropTypes.string.isRequired,
  };
  
  const documentColumns = [
    { Header: "Name", accessor: "name", width: "40%" },
    { Header: "Kategorie", accessor: "category", width: "15%" },
    { Header: "Datum", accessor: "date", width: "15%" },
    { Header: "Typ", accessor: "type", width: "10%" },
    { Header: "Größe", accessor: "size", width: "10%" },
    { 
      Header: "Download", 
      accessor: "link", 
      width: "10%",
      Cell: DownloadCell
    },
  ];

  const handleAddCustomer = async (customerData) => {
    try {
      await partnerService.createCustomer(customerData);
      setCreateError('');
      setShowAddCustomer(false);
      fetchData();
    } catch (error) {
      console.error('Fehler beim Erstellen des Kunden:', error);
      setCreateError(error?.response?.data?.error || 'Fehler beim Anlegen des Kunden. Bitte prüfen Sie die Eingaben.');
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.Vorname && customer.Vorname.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.Nachname && customer.Nachname.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const filteredDocuments = documents.filter(doc => 
    activeDocTab === 0 || doc.category === documentCategories[activeDocTab - 1]
  );

  return (
    <SoftBox py={3}>
      <SoftBox mb={4}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h3" fontWeight="medium" gutterBottom>
              Willkommen im Partnercenter
            </SoftTypography>
            <SoftTypography variant="body2" fontWeight="regular" mb={3}>
              Entdecken Sie die Vorteile unseres Partnerprogramms und erhalten Sie Einblicke in unsere Preisgestaltung.
            </SoftTypography>
            <Grid container spacing={3}>
              {/* Vorteile */}
              <Grid item xs={12} md={6}>
                <Card>
                  <SoftBox p={3}>
                    <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
                      Vorteile
                    </SoftTypography>
                    <SoftTypography variant="body2" fontWeight="regular">
                      - Exklusive Rabatte und Angebote<br />
                      - Zugang zum White Label Customer Portal<br />
                      - Persönlicher Support und Beratung sowie eigene Produkte
                    </SoftTypography>
                  </SoftBox>
                </Card>
              </Grid>
              {/* Preiseinblicke */}
              <Grid item xs={12} md={6}>
                <Card>
                  <SoftBox p={3}>
                    <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
                      Sonderkonditionen
                    </SoftTypography>
                    <SoftTypography variant="body2" fontWeight="regular">
                      Aktuell bieten wir allen Partnern 20% Rabatt auf alle IaaS- & SaaS-Produkten die zum Wiederverkauf vorgesehen sind an. Dieser Rabatt gilt für alle neuen Bestellungen und wird automatisch auf Ihre Rechnung angewendet.
                    </SoftTypography>
                  </SoftBox>
                </Card>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>
      </SoftBox>
      <SoftBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <SoftBox>
                    <SoftTypography variant="h4" fontWeight="medium">
                      {customers.length}
                    </SoftTypography>
                    <SoftTypography variant="button" color="text" fontWeight="regular">
                      Kunden
                    </SoftTypography>
                  </SoftBox>
                  <SoftBox
                    width="45px"
                    height="45px"
                    backgroundGradient="dark"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="xl"
                  >
                    <Icon fontSize="medium" color="white">
                      people
                    </Icon>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <SoftBox>
                    <SoftTypography variant="h4" fontWeight="medium">
                      {iaasStats.count || 0}
                    </SoftTypography>
                    <SoftTypography variant="button" color="text" fontWeight="regular">
                      Aktive IaaS-Services
                    </SoftTypography>
                  </SoftBox>
                  <SoftBox
                    width="45px"
                    height="45px"
                    backgroundGradient="info"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="xl"
                  >
                    <Icon fontSize="medium" color="white">
                      memory
                    </Icon>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <SoftBox>
                    <SoftTypography variant="h4" fontWeight="medium">
                      {saasStats.count || 0}
                    </SoftTypography>
                    <SoftTypography variant="button" color="text" fontWeight="regular">
                      Aktive SaaS-Services
                    </SoftTypography>
                  </SoftBox>
                  <SoftBox
                    width="45px"
                    height="45px"
                    backgroundGradient="success"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="xl"
                  >
                    <Icon fontSize="medium" color="white">
                      cloud
                    </Icon>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <SoftBox p={3}>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <SoftBox>
                    <SoftButton
                      variant="gradient"
                      color="primary"
                      onClick={() => setShowAddCustomer(true)}
                      startIcon={<Icon>add</Icon>}
                    >
                      Neuen Kunden anlegen
                    </SoftButton>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>

      <Card>
        <SoftBox p={3}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Kundenliste" />
            <Tab label="IaaS Services" />
            <Tab label="SaaS Services" />
          </Tabs>
          
          <SoftBox mt={3}>
            {activeTab === 0 && (
              <>
                <SoftBox mb={3} display="flex" justifyContent="space-between">
                  <SoftTypography variant="h5" fontWeight="medium">
                    Meine Kunden
                  </SoftTypography>
                  <SoftBox width="25%">
                    <SoftInput
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      icon={{ component: "search", direction: "left" }}
                    />
                  </SoftBox>
                </SoftBox>
                
                <SoftBox>
                  {isLoading ? (
                    <SoftTypography>Daten werden geladen...</SoftTypography>
                  ) : filteredCustomers.length > 0 ? (
                    <Grid container spacing={3}>
                      {filteredCustomers.map((customer) => (
                        <Grid item xs={12} md={6} key={customer.id}>
                          <Card>
                            <SoftBox p={3}>
                              <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                              >
                                <SoftTypography variant="h6" fontWeight="medium">
                                  {customer.company}
                                </SoftTypography>
                                <SoftBadge
                                  variant="contained"
                                  color={customer.istactive ? "success" : "error"}
                                  size="xs"
                                >
                                  {customer.istactive ? "Aktiv" : "Inaktiv"}
                                </SoftBadge>
                              </SoftBox>
                              
                              <SoftBox opacity={0.7}>
                                <SoftTypography variant="button" fontWeight="regular">
                                  {customer.Vorname} {customer.Nachname}
                                </SoftTypography>
                              </SoftBox>
                              <SoftBox opacity={0.7}>
                                <SoftTypography variant="button" fontWeight="regular">
                                  {customer.email}
                                </SoftTypography>
                              </SoftBox>
                              
                              <SoftBox mt={3} display="flex" justifyContent="flex-end">
                                <SoftBox mr={1}>
                                  <SoftButton
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    onClick={() => setPriceDialogCustomer(customer)}
                                  >
                                    <Icon style={{ marginRight: 4 }}>euro</Icon>
                                    Preise
                                  </SoftButton>
                                </SoftBox>
                                <SoftButton
                                  variant="outlined"
                                  color="info"
                                  size="small"
                                  onClick={() => {
                                    window.location.href = `/partner/customer/${customer.id}`;
                                  }}
                                >
                                  Details
                                </SoftButton>
                              </SoftBox>
                            </SoftBox>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <SoftBox textAlign="center" py={5}>
                      <SoftTypography variant="body2" color="text">
                        Keine Kunden gefunden
                      </SoftTypography>
                    </SoftBox>
                  )}
                </SoftBox>
              </>
            )}
            
            {activeTab === 1 && <CustomerServices serviceType="iaas" />}
            {activeTab === 2 && <CustomerServices serviceType="saas" />}
          </SoftBox>
        </SoftBox>
      </Card>

      {/* Partner Dokumente */}
      <SoftBox mt={4} mb={4}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h4" fontWeight="medium" gutterBottom>
              Partner Dokumente
            </SoftTypography>
            <SoftTypography variant="body2" fontWeight="regular" mb={3}>
              Hier finden Sie wichtige Dokumente für Partner wie Whitepapers, Präsentationen und aktuelle Preislisten.
            </SoftTypography>
            
            <Tabs 
              value={activeDocTab} 
              onChange={handleDocTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              textColor="secondary"
              indicatorColor="secondary"
            >
              <Tab label="Alle" />
              {documentCategories.map((category, index) => (
                <Tab key={index} label={category} />
              ))}
            </Tabs>
            
            <SoftBox mt={3}>
              <DataTable
                table={{
                  columns: documentColumns,
                  rows: filteredDocuments
                }}
                entriesPerPage={{
                  defaultValue: 5,
                  entries: [5, 10, 15],
                }}
                canSearch
              />
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>

      {/* White-Label / Branding */}
      <SoftBox mt={4} mb={4}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h4" fontWeight="medium" gutterBottom>
              White-Label / Branding
            </SoftTypography>
            <SoftTypography variant="body2" fontWeight="regular" mb={3}>
              Konfigurieren Sie hier Ihr eigenes Branding: Logo, Favicon, Markenname, Primärfarbe und sichtbare Produkte für Ihre Kunden.
            </SoftTypography>
            <PartnerBrandingSettings />
          </SoftBox>
        </Card>
      </SoftBox>

      <CustomerPriceDialog
        open={priceDialogCustomer !== null}
        onClose={() => setPriceDialogCustomer(null)}
        customer={priceDialogCustomer}
      />

      <Dialog
        open={showAddCustomer}
        onClose={() => { setShowAddCustomer(false); setCreateError(''); }}
        maxWidth="md"
        fullWidth
      >
        {createError && (
          <SoftBox px={3} pt={2}>
            <SoftTypography variant="body2" color="error">
              {createError}
            </SoftTypography>
          </SoftBox>
        )}
        <CustomerAddForm
          onSubmit={handleAddCustomer}
          onCancel={() => { setShowAddCustomer(false); setCreateError(''); }}
        />
      </Dialog>
    </SoftBox>
  );
};

// PropTypes validation
PartnerDashboard.propTypes = {
  userId: PropTypes.string.isRequired,
};

export default PartnerDashboard;